(function initCalendarCartPreview() {
  console.log('Initializing calendar cart preview');

  const STORAGE_KEY = 'calendar-cart-preview-by-assignment-offer-id-v1';
  const GENERIC_STORAGE_KEY = 'offer-cart-preview-by-offer-id-v1';
  const KEEPSAKE_CART_PREVIEW_UPDATED_EVENT = 'keepsake-cart-preview-cache:updated';
  const calendarOfferUrls = Array.isArray(window.NEW_CALENDAR_OFFER_URLS) ? window.NEW_CALENDAR_OFFER_URLS : [];
  let latestCart = null;
  let applyTimer = null;
  let cartDrawerObserver = null;

  function normalizeString(value) {
    return String(value || '').trim();
  }

  function normalizeIdentity(value) {
    return normalizeString(value).toLowerCase();
  }

  function normalizePath(url) {
    try {
      return new URL(url || '', window.location.origin).pathname;
    } catch (error) {
      return String(url || '').split('?')[0];
    }
  }

  function stripProductsPrefix(path) {
    return String(path || '').replace(/^\/products(?=\/)/, '');
  }

  function productHandleFromUrl(url) {
    const path = stripProductsPrefix(normalizePath(url));
    const match = path.match(/^\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  function variantIdFromUrl(url) {
    try {
      return normalizeString(new URL(url || '', window.location.origin).searchParams.get('variant'));
    } catch (error) {
      return '';
    }
  }

  function getPreviewMap(storageKey) {
    try {
      const parsed = JSON.parse(localStorage.getItem(storageKey) || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  function getPreviewUrlForAssignmentOfferId(assignmentOfferId) {
    if (!assignmentOfferId) {
      return '';
    }

    return getPreviewMap(STORAGE_KEY)[String(assignmentOfferId)] || '';
  }

  function getVariantCachedPreviewUrl(offerId, variantId) {
    if (!offerId) {
      return '';
    }

    const previewMap = getPreviewMap(GENERIC_STORAGE_KEY);
    return previewMap[`${offerId}:${variantId}`] || previewMap[String(offerId)] || '';
  }

  function applyImageToElement(image, imageUrl, options = {}) {
    if (!image || !imageUrl) {
      return;
    }

    if (image.src === imageUrl && !image.srcset) {
      return;
    }

    image.src = imageUrl;
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');

    if (options.objectFitContain) {
      image.style.objectFit = 'contain';
    } else {
      image.style.removeProperty('object-fit');
    }
  }

  function isCalendarCartItem(item) {
    const path = normalizePath(item?.url);
    if (!path || !calendarOfferUrls.length) {
      return false;
    }

    const candidatePaths = [path, stripProductsPrefix(path)].filter(Boolean);

    return candidatePaths.some((candidatePath) =>
      calendarOfferUrls.some((offerUrl) => candidatePath === offerUrl || candidatePath.startsWith(`${offerUrl}?`) || candidatePath.startsWith(offerUrl))
    );
  }

  function parseAttribution(item) {
    const attribution = item?.properties?._attribution;
    if (!attribution) {
      return {};
    }

    try {
      const parsed = typeof attribution === 'string' ? JSON.parse(attribution) : attribution;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  function getAssignmentOfferId(item) {
    const attribution = parseAttribution(item);
    return normalizeString(attribution.assignmentOfferId || attribution.assignment_offer_id);
  }

  function getOfferId(item) {
    const attribution = parseAttribution(item);
    return normalizeString(attribution.offer_id || attribution.offerId);
  }

  function getCartPreviewImageUrl(item) {
    const properties = item?.properties;
    if (!properties || typeof properties !== 'object') {
      return '';
    }

    return normalizeString(properties._cart_preview_image_url);
  }

  function getItemPreviewUrl(item) {
    const directPreviewUrl = getCartPreviewImageUrl(item);
    if (directPreviewUrl) {
      return directPreviewUrl;
    }

    if (isCalendarCartItem(item)) {
      const calendarPreviewUrl = getPreviewUrlForAssignmentOfferId(getAssignmentOfferId(item));
      if (calendarPreviewUrl) {
        return calendarPreviewUrl;
      }
    }

    return getVariantCachedPreviewUrl(getOfferId(item), normalizeString(item?.variant_id || item?.id));
  }

  function isIgnoredCartItem(item) {
    if (!item || typeof item !== 'object') {
      return false;
    }

    if (item.product_type === 'Kaching Cart Upsell Toggle') {
      return true;
    }

    const kachingCartProperty = item?.properties?._kaching_cart;
    if (!kachingCartProperty) {
      return false;
    }

    try {
      const parsed = typeof kachingCartProperty === 'string' ? JSON.parse(kachingCartProperty) : kachingCartProperty;
      return Boolean(parsed?.isUpsellToggle);
    } catch (error) {
      return false;
    }
  }

  function cartItemIdentity(item) {
    const url = normalizeString(item?.url);
    return {
      key: normalizeString(item?.key),
      variantId: normalizeString(item?.variant_id || item?.id),
      productId: normalizeString(item?.product_id),
      handle: normalizeIdentity(item?.handle || productHandleFromUrl(url)),
      path: normalizePath(url),
      title: normalizeIdentity(item?.product_title || item?.title),
    };
  }

  function identityMatches(rowIdentity, itemIdentity, level) {
    if (level === 'variant' && rowIdentity.variantId && itemIdentity.variantId) {
      return rowIdentity.variantId === itemIdentity.variantId;
    }

    if (level === 'product' && rowIdentity.productId && itemIdentity.productId) {
      return rowIdentity.productId === itemIdentity.productId;
    }

    if (level === 'handle' && rowIdentity.handle && itemIdentity.handle) {
      return rowIdentity.handle === itemIdentity.handle;
    }

    if (level === 'path' && rowIdentity.path && itemIdentity.path) {
      return rowIdentity.path === itemIdentity.path || stripProductsPrefix(rowIdentity.path) === stripProductsPrefix(itemIdentity.path);
    }

    if (level === 'title' && rowIdentity.title && itemIdentity.title) {
      return rowIdentity.title === itemIdentity.title;
    }

    return false;
  }

  function findMatchingCartItem(rowIdentity, candidates, usedKeys) {
    for (const level of ['variant', 'product', 'handle', 'path', 'title']) {
      const match = candidates.find((candidate) => {
        const key = candidate.identity.key || `${candidate.identity.variantId}:${candidate.identity.productId}:${candidate.identity.handle}`;
        return !usedKeys.has(key) && identityMatches(rowIdentity, candidate.identity, level);
      });

      if (match) {
        const key = match.identity.key || `${match.identity.variantId}:${match.identity.productId}:${match.identity.handle}`;
        usedKeys.add(key);
        return match;
      }
    }

    return null;
  }

  const cartRenderers = [
    {
      name: 'kaching',
      waitSelector: '.kaching-cart-item__image',
      getRows() {
        return Array.from(document.querySelectorAll('.kaching-cart-item'));
      },
      getImage(row) {
        return row?.querySelector('.kaching-cart-item__image') || null;
      },
      getIdentity(row) {
        const link = row?.querySelector('.kaching-cart-item__link, .kaching-cart-item__title');
        const href = link?.getAttribute('href') || '';
        return {
          variantId: variantIdFromUrl(href),
          productId: normalizeString(row?.getAttribute('data-product-id')),
          handle: normalizeIdentity(row?.getAttribute('data-product-handle') || productHandleFromUrl(href)),
          path: normalizePath(href),
          title: normalizeIdentity(row?.querySelector('.kaching-cart-item__title')?.textContent),
        };
      },
    },
    {
      name: 'loess',
      waitSelector: '#MainCartItems .cart-item__image',
      getRows() {
        return Array.from(document.querySelectorAll('#MainCartItems .cart-item'));
      },
      getImage(row) {
        return row?.querySelector('.cart-item__image') || null;
      },
      getIdentity(row) {
        const link = row?.querySelector('a[href*="/products/"]');
        const href = link?.getAttribute('href') || '';
        return {
          variantId: variantIdFromUrl(href),
          productId: normalizeString(row?.getAttribute('data-product-id')),
          handle: normalizeIdentity(row?.getAttribute('data-product-handle') || productHandleFromUrl(href)),
          path: normalizePath(href),
          title: normalizeIdentity(row?.querySelector('.cart-item__name, .cart-item__details a')?.textContent),
        };
      },
    },
  ];

  function getActiveCartRenderer() {
    return cartRenderers.find((renderer) => renderer.getRows().length > 0) || null;
  }

  function waitForElement(selector, callback, timeoutMs = 10000) {
    const existing = document.querySelector(selector);
    if (existing) {
      callback(existing);
      return;
    }

    const observer = new MutationObserver(() => {
      const element = document.querySelector(selector);
      if (!element) {
        return;
      }

      observer.disconnect();
      callback(element);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), timeoutMs);
  }

  function waitForCartRenderer(callback, timeoutMs = 10000) {
    const activeRenderer = getActiveCartRenderer();
    if (activeRenderer) {
      callback(activeRenderer);
      return;
    }

    const observer = new MutationObserver(() => {
      const renderer = getActiveCartRenderer();
      if (!renderer) {
        return;
      }

      observer.disconnect();
      callback(renderer);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), timeoutMs);
  }

  function applyPreviewImages(cart) {
    if (!cart?.items?.length) {
      return;
    }

    const renderer = getActiveCartRenderer();
    if (!renderer) {
      waitForCartRenderer(() => applyPreviewImages(cart));
      return;
    }

    const candidates = cart.items
      .filter((item) => !isIgnoredCartItem(item))
      .map((item) => ({ item, identity: cartItemIdentity(item), previewUrl: getItemPreviewUrl(item) }))
      .filter((candidate) => candidate.previewUrl);

    if (!candidates.length) {
      return;
    }

    const usedKeys = new Set();
    renderer.getRows().forEach((row) => {
      const image = renderer.getImage(row);
      if (!image) {
        return;
      }

      const matched = findMatchingCartItem(renderer.getIdentity(row), candidates, usedKeys);
      if (!matched) {
        return;
      }

      applyImageToElement(image, matched.previewUrl, { objectFitContain: true });
    });
  }

  function scheduleApply(delayMs = 0) {
    clearTimeout(applyTimer);
    applyTimer = setTimeout(() => {
      applyPreviewImages(latestCart);
    }, delayMs);
  }

  function scheduleApplyPasses() {
    scheduleApply(0);
    setTimeout(() => applyPreviewImages(latestCart), 120);
    setTimeout(() => applyPreviewImages(latestCart), 400);
  }

  async function fetchCart() {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!response.ok) {
        return;
      }

      latestCart = await response.json();
      scheduleApplyPasses();
    } catch (error) {
      console.error('Failed to load cart for calendar preview images', error);
    }
  }

  function handleCartDrawerOpen(drawer) {
    if (!drawer) {
      return;
    }

    const isOpen = drawer.hasAttribute('open') && drawer.getAttribute('open') !== 'false';
    if (!isOpen) {
      return;
    }

    fetchCart();
  }

  function observeKachingCartDrawer() {
    waitForElement('kaching-cart-drawer#cart-drawer', (drawer) => {
      handleCartDrawerOpen(drawer);

      if (cartDrawerObserver) {
        cartDrawerObserver.disconnect();
      }

      cartDrawerObserver = new MutationObserver((mutations) => {
        const shouldFetch = mutations.some((mutation) => mutation.type === 'attributes' && mutation.attributeName === 'open');
        if (shouldFetch) {
          handleCartDrawerOpen(drawer);
          return;
        }

        if (latestCart) {
          scheduleApply(80);
        }
      });

      cartDrawerObserver.observe(drawer, {
        attributes: true,
        attributeFilter: ['open'],
        childList: true,
        subtree: true,
      });
    }, 30000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      fetchCart();
      observeKachingCartDrawer();
    }, { once: true });
  } else {
    fetchCart();
    observeKachingCartDrawer();
  }

  document.documentElement.addEventListener('cart:updated', (event) => {
    latestCart = event.detail?.cart || latestCart;
    scheduleApplyPasses();
  });

  document.documentElement.addEventListener(KEEPSAKE_CART_PREVIEW_UPDATED_EVENT, () => {
    if (latestCart) {
      scheduleApplyPasses();
      return;
    }
    fetchCart();
  });
})();
